import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { X, Loader2, Check, MessageSquare, AlertTriangle, ChevronRight, ChevronDown, Clock, Download, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  åben:       { bg: "bg-yellow-500/10", text: "text-yellow-600 dark:text-yellow-400" },
  afsluttet:  { bg: "bg-blue-500/10",   text: "text-blue-600 dark:text-blue-400" },
  godkendt:   { bg: "bg-green-500/10",  text: "text-green-600 dark:text-green-400" },
  afvist:     { bg: "bg-red-500/10",    text: "text-red-600 dark:text-red-400" },
  // engelske status-værdier fra clock_records
  open:       { bg: "bg-yellow-500/10", text: "text-yellow-600 dark:text-yellow-400" },
  paused:     { bg: "bg-yellow-500/10", text: "text-yellow-600 dark:text-yellow-400" },
  completed:  { bg: "bg-blue-500/10",   text: "text-blue-600 dark:text-blue-400" },
  approved:   { bg: "bg-green-500/10",  text: "text-green-600 dark:text-green-400" },
  rejected:   { bg: "bg-red-500/10",    text: "text-red-600 dark:text-red-400" },
};

const STATUS_LABEL = {
  open: "Åben", paused: "Pause", completed: "Afsluttet", approved: "Godkendt", rejected: "Afvist",
  åben: "Åben", afsluttet: "Afsluttet", godkendt: "Godkendt", afvist: "Afvist",
};

// "Afventer godkendelse" = alt der ikke er godkendt/afvist.
const APPROVED = new Set(["approved", "godkendt"]);
const REJECTED = new Set(["rejected", "afvist"]);
function erAfventer(status) { return !APPROVED.has(status) && !REJECTED.has(status); }

const UGEDAGE = ["man", "tir", "ons", "tor", "fre", "lør", "søn"];
const MND_FULL = ["januar","februar","marts","april","maj","juni","juli","august","september","oktober","november","december"];

function isoFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function mondayOf(iso) {
  const d = new Date(iso + "T00:00:00");
  const dow = (d.getDay() + 6) % 7; // man=0
  d.setDate(d.getDate() - dow);
  return isoFromDate(d);
}
function isoWeekNum(mondayIso) {
  // ISO-ugenummer: torsdagen i ugen afgør årets uge-nr.
  const d = new Date(mondayIso + "T00:00:00");
  const thu = new Date(d); thu.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const jan1 = new Date(thu.getFullYear(), 0, 1);
  return Math.ceil(((thu - jan1) / 86400000 + 1) / 7);
}
function weekLabel(mondayIso) {
  const d = new Date(mondayIso + "T00:00:00");
  const thu = new Date(d); thu.setDate(d.getDate() + 3);
  const mnd = MND_FULL[thu.getMonth()];
  return `Uge ${isoWeekNum(mondayIso)} · ${mnd.charAt(0).toUpperCase() + mnd.slice(1)} ${thu.getFullYear()}`;
}
function fmtTid(ts) {
  if (!ts) return "—";
  // Læs HH:MM direkte fra ISO-strengen (efter T) — undgår new Date() browserkonvertering.
  // Tider er altid dansk lokaltid med korrekt offset (fx +02:00).
  const m = String(ts).match(/T(\d{2}:\d{2})/);
  if (m) return m[1];
  return "—";
}
function fmtDato(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleDateString("da-DK", { day: "numeric", month: "short" }); }
  catch { return ts; }
}
function fmtTimer(t) {
  const n = parseFloat(t);
  if (!isFinite(n)) return "0 t";
  return `${(Math.round(n * 10) / 10).toLocaleString("da-DK")} t`;
}
// Dag-label "man 8" ud fra en YYYY-MM-DD.
function dagLabel(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return `${UGEDAGE[(d.getDay()+6)%7]} ${d.getDate()}`;
  } catch { return iso; }
}

const FRAVAERS_TYPER = new Set(["Syg", "Ferie", "Fri"]);
// Opf. #36-regel: stempling overtager planlagt vagt. Genbrugt her.
function dagTimer(vagter = [], stempler = []) {
  if (stempler.length > 0) return stempler.reduce((s, r) => s + (parseFloat(r.timer_actual) || 0), 0);
  return vagter.reduce((s, v) => FRAVAERS_TYPER.has(v.vagttype) ? s : s + (parseFloat(v.timer) || 0), 0);
}

function EditModal({ record, token, onSaved, onClose }) {
  const [ind, setInd] = useState(fmtTid(record.clock_in) === "—" ? "" : fmtTid(record.clock_in));
  const [ud, setUd] = useState(fmtTid(record.clock_out) === "—" ? "" : fmtTid(record.clock_out));
  const [note, setNote] = useState(record.note || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/clock-records/${record.clock_id}`,
        { clock_in: ind, clock_out: ud, note },
        { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
      onClose();
    } catch { alert("Kunne ikke gemme"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-sm">Rediger stempling — {record.medarbejder}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 flex flex-col gap-4">
          <p className="text-[13px] text-muted-foreground">{fmtDato(record.clock_in)}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Stempling ind</label>
              <input type="time" value={ind} onChange={e => setInd(e.target.value)}
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Stempling ud</label>
              <input type="time" value={ud} onChange={e => setUd(e.target.value)}
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Note</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] resize-y" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Gem
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Underfane 1: Stempling (eksisterende tabel-visning) ───────────────────
function StemplingTab({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const defaultFra = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); })();
  const [fraDato, setFraDato] = useState(defaultFra);
  const [tilDato, setTilDato] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [visAfvigelser, setVisAfvigelser] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = visAfvigelser ? `${API}/admin/clock-records/afvigelser` : `${API}/admin/clock-records`;
      const params = {};
      if (search) params.medarbejder = search;
      if (fraDato) params.fra_dato = fraDato;
      if (tilDato) params.til_dato = tilDato;
      if (statusFilter) params.status = statusFilter;
      const r = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` }, params });
      setRecords(r.data.records || r.data || []);
    } catch { setRecords([]); }
    setLoading(false);
  }, [token, search, fraDato, tilDato, statusFilter, visAfvigelser]);

  useEffect(() => { load(); }, [load]);

  const handleGodkend = async (clock_id) => {
    try { await axios.post(`${API}/admin/clock-records/${clock_id}/godkend`, {}, { headers: { Authorization: `Bearer ${token}` } }); load(); }
    catch { alert("Kunne ikke godkende"); }
  };
  const handleAfvis = async (clock_id) => {
    const aarsag = window.prompt("Angiv årsag til afvisning:");
    if (aarsag === null) return;
    try { await axios.post(`${API}/admin/clock-records/${clock_id}/afvis`, { aarsag }, { headers: { Authorization: `Bearer ${token}` } }); load(); }
    catch { alert("Kunne ikke afvise"); }
  };

  const afventerCount = records.filter(r => erAfventer(r.status)).length;
  const totalTimer = records.reduce((s, r) => s + parseFloat(r.timer_actual || 0), 0);

  return (
    <>
      {editRecord && <EditModal record={editRecord} token={token} onSaved={load} onClose={() => setEditRecord(null)} />}

      {afventerCount > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-yellow-500/30 bg-yellow-500/5 px-4 py-3 mb-5">
          <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />
          <span className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">
            {afventerCount} stemplinger afventer godkendelse · {totalTimer.toFixed(1)} timer samlet
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Medarbejder</label>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Søg navn..."
            className="bg-secondary border border-border rounded-lg px-3 h-8 text-sm w-48 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Fra dato</label>
          <input type="date" value={fraDato} onChange={e => setFraDato(e.target.value)}
            className="bg-secondary border border-border rounded-lg px-3 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Til dato</label>
          <input type="date" value={tilDato} onChange={e => setTilDato(e.target.value)}
            className="bg-secondary border border-border rounded-lg px-3 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="bg-secondary border border-border rounded-lg px-3 h-8 text-sm focus:outline-none">
            <option value="">Alle</option>
            <option value="open">Åben</option>
            <option value="completed">Afsluttet</option>
            <option value="approved">Godkendt</option>
            <option value="rejected">Afvist</option>
          </select>
        </div>
        <button onClick={() => setVisAfvigelser(v => !v)}
          title="Vis kun stemplinger med en note eller en afvigelse"
          className={`h-8 px-3 rounded-lg text-[13px] font-semibold border transition-colors ${visAfvigelser ? "bg-amber-500/10 border-amber-500/40 text-amber-500" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
          Vis kun afvigelser
        </button>
        <button onClick={load} className="h-8 px-4 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:bg-[#e02560] transition-colors">Hent</button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-12"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /><p className="text-[13px] text-muted-foreground mt-3">Henter stemplinger…</p></div>
      ) : records.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-muted-foreground">
          <CheckCircle2 className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-sm">Ingen stemplinger fundet i den valgte periode.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                {["Medarbejder", "Dato", "Ind", "Ud", "Timer", "Note", "Status", "Handlinger"].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => {
                const st = STATUS_STYLES[r.status] || { bg: "bg-secondary", text: "text-muted-foreground" };
                return (
                  <tr key={r.clock_id} className={`border-b border-border hover:bg-secondary/30 transition-colors ${i % 2 === 0 ? "" : "bg-secondary/10"}`}>
                    <td className="py-3 px-4 font-medium">{r.medarbejder || "—"}</td>
                    <td className="py-3 px-4 text-muted-foreground tabular-nums">{fmtDato(r.clock_in)}</td>
                    <td className="py-3 px-4 tabular-nums">{fmtTid(r.clock_in)}</td>
                    <td className="py-3 px-4 tabular-nums">{fmtTid(r.clock_out)}</td>
                    <td className="py-3 px-4 tabular-nums">{r.timer_actual ? parseFloat(r.timer_actual).toFixed(1) + "t" : "—"}</td>
                    <td className="py-3 px-4">{r.note ? <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" title={r.note} /> : null}</td>
                    <td className="py-3 px-4">
                      <span title={`Status: ${STATUS_LABEL[r.status] || r.status}`} className={`inline-flex items-center text-[13px] font-semibold rounded-full px-2.5 py-1 ${st.bg} ${st.text}`}>
                        {STATUS_LABEL[r.status] || r.status || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setEditRecord(r)} className="px-2.5 py-1 rounded-lg text-[13px] font-semibold bg-secondary hover:bg-secondary/80 transition-colors">Rediger</button>
                        {!APPROVED.has(r.status) && (
                          <button onClick={() => handleGodkend(r.clock_id)} className="px-2.5 py-1 rounded-lg text-[13px] font-semibold bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-colors">Godkend</button>
                        )}
                        {!REJECTED.has(r.status) && (
                          <button onClick={() => handleAfvis(r.clock_id)} className="px-2.5 py-1 rounded-lg text-[13px] font-semibold bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors">Afvis</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Underfane 2: Timesedler (Planday-inspireret, uge pr. medarbejder) ──────
function TimesedlerTab({ token, isAdmin }) {
  // Periode = den valgte uge (mandag-søndag). Default: indeværende uge.
  const [mandag, setMandag] = useState(() => mondayOf(isoFromDate(new Date())));
  const [records, setRecords] = useState([]);
  const [vagter, setVagter] = useState([]);
  const [stamMap, setStamMap] = useState({}); // navn → afdeling (by-fallback)
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("afventer"); // afventer | godkendt | alle
  const [byFilter, setByFilter] = useState("Alle");
  const [valgte, setValgte] = useState(new Set()); // valgte medarbejder-nøgler (navn)
  const [busy, setBusy] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [foldUd, setFoldUd] = useState(new Set());

  const ugeDage = useMemo(() => {
    const out = [];
    const base = new Date(mandag + "T00:00:00");
    for (let i = 0; i < 7; i++) { const d = new Date(base); d.setDate(base.getDate() + i); out.push(isoFromDate(d)); }
    return out;
  }, [mandag]);
  const fraIso = ugeDage[0];
  const tilIso = ugeDage[6];

  const load = useCallback(async () => {
    setLoading(true);
    setValgte(new Set());
    try {
      const hdrs = { headers: { Authorization: `Bearer ${token}` } };
      const [recRes, vagtRes, stamRes] = await Promise.all([
        axios.get(`${API}/admin/clock-records`, { ...hdrs, params: { fra_dato: fraIso, til_dato: tilIso } }).catch(() => ({ data: { records: [] } })),
        // Planlagte vagter kræver kan_se_vagtplan; pure stempling-godkendere må gerne fejle → tom liste.
        axios.get(`${API}/hub/vagtplan/alle`, { ...hdrs, params: { fra_dato: fraIso, til_dato: tilIso } }).catch(() => ({ data: { vagter: [] } })),
        axios.get(`${API}/hub/vagtplan/medarbejdere-liste`, hdrs).catch(() => ({ data: { medarbejdere: [] } })),
      ]);
      setRecords(recRes.data.records || recRes.data || []);
      setVagter(vagtRes.data.vagter || []);
      const sm = {};
      // /hub/vagtplan/medarbejdere-liste returnerer byen i feltet `city`.
      (stamRes.data.medarbejdere || stamRes.data || []).forEach(m => { if (m.name) sm[m.name] = m.city || m.by || m.afdeling || ""; });
      setStamMap(sm);
    } catch { setRecords([]); setVagter([]); }
    setLoading(false);
  }, [token, fraIso, tilIso]);

  useEffect(() => { load(); }, [load]);

  // By for et record/vagt: eksplicit felt, ellers medarbejderens afdeling.
  const byForNavn = useCallback((navn, eksplicit) => (eksplicit || stamMap[navn] || "").trim(), [stamMap]);

  // Byg én række pr. medarbejder for ugen.
  const rækker = useMemo(() => {
    const m = {}; // navn → { records:[], vagter:[], by }
    records.forEach(r => {
      const n = r.medarbejder || "—";
      (m[n] = m[n] || { records: [], vagter: [], navn: n }).records.push(r);
    });
    vagter.forEach(v => {
      const n = v.medarbejder || "—";
      (m[n] = m[n] || { records: [], vagter: [], navn: n }).vagter.push(v);
    });
    const out = Object.values(m).map(row => {
      // Per-dag gruppering
      const perDag = {};
      ugeDage.forEach(iso => { perDag[iso] = { vagter: [], records: [] }; });
      row.vagter.forEach(v => { if (perDag[v.dato]) perDag[v.dato].vagter.push(v); });
      row.records.forEach(r => { if (perDag[r.dato]) perDag[r.dato].records.push(r); });

      let planlagt = 0, stemplet = 0;
      ugeDage.forEach(iso => {
        const dv = perDag[iso].vagter, dr = perDag[iso].records;
        planlagt += dv.reduce((s, v) => FRAVAERS_TYPER.has(v.vagttype) ? s : s + (parseFloat(v.timer) || 0), 0);
        stemplet += dr.reduce((s, r) => s + (parseFloat(r.timer_actual) || 0), 0);
      });
      const harAdhoc = row.records.some(r => r.er_adhoc || !r.vagt_id);
      const harAutoUd = row.records.some(r => r.auto_udstemplet);
      const harNoteKraevet = row.records.some(r => r.auto_udstemplet && !((r.note || "").trim()));
      // Status for ugen: afventer hvis NOGEN record afventer; ellers godkendt (hvis nogen) ellers tom.
      const afventendeRecords = row.records.filter(r => erAfventer(r.status));
      const ugeStatus = afventendeRecords.length > 0 ? "afventer"
        : row.records.some(r => APPROVED.has(r.status)) ? "godkendt"
        : "alle";
      const by = byForNavn(row.navn, row.records.find(r => r.by)?.by || row.vagter.find(v => v.city)?.city);
      return {
        navn: row.navn, by,
        planlagt: Math.round(planlagt * 10) / 10,
        stemplet: Math.round(stemplet * 10) / 10,
        saldo: Math.round((stemplet - planlagt) * 10) / 10,
        perDag, ugeDage,
        records: row.records, vagter: row.vagter,
        harAdhoc, harAutoUd, harNoteKraevet, ugeStatus,
        afventendeIds: afventendeRecords.map(r => r.clock_id),
      };
    });
    return out.sort((a, b) => a.navn.localeCompare(b.navn, "da"));
  }, [records, vagter, ugeDage, byForNavn]);

  const byer = useMemo(() => {
    const s = new Set();
    rækker.forEach(r => { if (r.by) s.add(r.by); });
    return ["Alle", ...[...s].sort()];
  }, [rækker]);

  const synligeRækker = useMemo(() => {
    return rækker.filter(r => {
      if (byFilter !== "Alle" && r.by !== byFilter) return false;
      if (tab === "afventer" && r.ugeStatus !== "afventer") return false;
      if (tab === "godkendt" && r.ugeStatus !== "godkendt") return false;
      // Vis kun rækker der har NOGET (vagt eller stempling)
      return r.records.length > 0 || r.vagter.length > 0;
    });
  }, [rækker, byFilter, tab]);

  const afventerAntal = rækker.filter(r => r.ugeStatus === "afventer").length;
  const godkendtAntal = rækker.filter(r => r.ugeStatus === "godkendt").length;

  const toggleFold = (navn) => setFoldUd(prev => { const n = new Set(prev); n.has(navn) ? n.delete(navn) : n.add(navn); return n; });
  const toggleValgt = (navn) => setValgte(prev => { const n = new Set(prev); n.has(navn) ? n.delete(navn) : n.add(navn); return n; });
  const valgAlle = () => {
    const medAfventende = synligeRækker.filter(r => r.afventendeIds.length > 0).map(r => r.navn);
    setValgte(prev => prev.size === medAfventende.length ? new Set() : new Set(medAfventende));
  };

  // Godkend en liste af clock_ids sekventielt.
  const godkendIds = async (ids) => {
    for (const id of ids) {
      await axios.post(`${API}/admin/clock-records/${id}/godkend`, {}, { headers: { Authorization: `Bearer ${token}` } });
    }
  };

  const handleGodkendUge = async (row) => {
    if (!row.afventendeIds.length) return;
    if (row.harNoteKraevet) {
      if (!window.confirm("Ugen har auto-udstemplede stemplinger uden note. Godkend alligevel?")) return;
    }
    setBusy(true);
    try { await godkendIds(row.afventendeIds); await load(); }
    catch { alert("Kunne ikke godkende ugen"); }
    setBusy(false);
  };

  const handleAfvisUge = async (row) => {
    if (!row.afventendeIds.length) return;
    const aarsag = window.prompt(`Afvis ${row.afventendeIds.length} stemplinger for ${row.navn}. Angiv årsag:`);
    if (aarsag === null) return;
    setBusy(true);
    try {
      for (const id of row.afventendeIds) {
        await axios.post(`${API}/admin/clock-records/${id}/afvis`, { aarsag }, { headers: { Authorization: `Bearer ${token}` } });
      }
      await load();
    } catch { alert("Kunne ikke afvise ugen"); }
    setBusy(false);
  };

  const handleBulkGodkend = async () => {
    const ids = synligeRækker.filter(r => valgte.has(r.navn)).flatMap(r => r.afventendeIds);
    if (!ids.length) { alert("Ingen valgte uger med afventende stemplinger."); return; }
    if (!window.confirm(`Godkend ${ids.length} stemplinger for ${valgte.size} medarbejdere?`)) return;
    setBusy(true);
    try { await godkendIds(ids); await load(); }
    catch { alert("Kunne ikke godkende"); }
    setBusy(false);
  };

  const handleBulkAfvis = async () => {
    const valgteRows = synligeRækker.filter(r => valgte.has(r.navn));
    const ids = valgteRows.flatMap(r => r.afventendeIds);
    if (!ids.length) { alert("Ingen valgte uger med afventende stemplinger."); return; }
    const aarsag = window.prompt(`Afvis ${ids.length} stemplinger for ${valgteRows.length} medarbejdere. Angiv årsag:`);
    if (aarsag === null) return;
    setBusy(true);
    try {
      for (const id of ids) {
        await axios.post(`${API}/admin/clock-records/${id}/afvis`, { aarsag }, { headers: { Authorization: `Bearer ${token}` } });
      }
      await load();
    } catch { alert("Kunne ikke afvise"); }
    setBusy(false);
  };

  // CSV-eksport for den viste uge.
  const eksporter = () => {
    const head = ["Medarbejder", "By", "Uge", "Planlagt (t)", "Stemplet (t)", "Saldo (t)", "Status"];
    const lines = [head.join(";")];
    synligeRækker.forEach(r => {
      lines.push([r.navn, r.by, isoWeekNum(mandag), r.planlagt, r.stemplet, r.saldo, r.ugeStatus].join(";"));
    });
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `timesedler-uge${isoWeekNum(mandag)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const skiftUge = (delta) => {
    const d = new Date(mandag + "T00:00:00");
    d.setDate(d.getDate() + delta * 7);
    setMandag(isoFromDate(d));
  };

  return (
    <>
      {editRecord && <EditModal record={editRecord} token={token} onSaved={load} onClose={() => setEditRecord(null)} />}

      {/* Uge-navigation + titel */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => skiftUge(-1)} title="Forrige uge" className="h-8 w-8 flex items-center justify-center rounded-lg border border-border hover:bg-secondary transition-colors">‹</button>
          <div className="text-sm font-bold tracking-tight min-w-[180px] text-center">{weekLabel(mandag).toUpperCase()}</div>
          <button onClick={() => skiftUge(1)} title="Næste uge" className="h-8 w-8 flex items-center justify-center rounded-lg border border-border hover:bg-secondary transition-colors">›</button>
          <button onClick={() => setMandag(mondayOf(isoFromDate(new Date())))} className="h-8 px-3 rounded-lg text-[13px] font-semibold border border-border hover:bg-secondary transition-colors">I dag</button>
        </div>
        <div className="flex items-center gap-2">
          <select value={byFilter} onChange={e => setByFilter(e.target.value)} title="Filtrér på by"
            className="bg-secondary border border-border rounded-lg px-3 h-8 text-sm focus:outline-none">
            {byer.map(b => <option key={b} value={b}>{b === "Alle" ? "Alle byer" : b}</option>)}
          </select>
          <button onClick={eksporter} title="Eksportér den viste uge som CSV"
            className="h-8 px-3 rounded-lg text-[13px] font-semibold border border-border hover:bg-secondary transition-colors inline-flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> Eksport
          </button>
        </div>
      </div>

      {/* Status-tabs */}
      <div className="flex items-center gap-2 mb-4">
        {[
          { key: "afventer", label: `Afventer (${afventerAntal})` },
          { key: "godkendt", label: `Godkendt (${godkendtAntal})` },
          { key: "alle", label: "Alle" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${tab === t.key ? "bg-foreground text-background" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-12"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /><p className="text-[13px] text-muted-foreground mt-3">Henter timesedler…</p></div>
      ) : synligeRækker.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-muted-foreground">
          <CheckCircle2 className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-sm">{tab === "afventer" ? "Ingen uger afventer godkendelse — alt er styr på 🎉" : "Ingen timesedler i den valgte uge."}</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
          {synligeRækker.map(row => {
            const er = foldUd.has(row.navn);
            const kanVælges = row.afventendeIds.length > 0;
            const saldoFarve = row.saldo > 0.05 ? "text-amber-600 dark:text-amber-400" : row.saldo < -0.05 ? "text-red-600 dark:text-red-400" : "text-muted-foreground";
            return (
              <div key={row.navn} className={`${valgte.has(row.navn) ? "bg-[#FF2A6D]/5" : ""}`}>
                {/* Sammenfattet uge-linje */}
                <div className="flex items-start gap-3 px-4 py-3">
                  <input type="checkbox" disabled={!kanVælges} checked={valgte.has(row.navn)} onChange={() => toggleValgt(row.navn)}
                    title={kanVælges ? "Vælg ugen til bulk-handling" : "Ingen afventende stemplinger"}
                    className="mt-1 accent-[#FF2A6D] disabled:opacity-30" />
                  <button onClick={() => toggleFold(row.navn)} className="mt-0.5 text-muted-foreground hover:text-foreground" title={er ? "Fold sammen" : "Fold ud — dag for dag"}>
                    {er ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{row.navn}</span>
                      {row.by && <span className="text-[13px] text-muted-foreground">· {row.by}</span>}
                      {row.ugeStatus === "afventer" && (
                        <span title="Ugen har stemplinger der afventer godkendelse" className="text-[12px] font-semibold rounded-full px-2 py-0.5 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400">Afventer</span>
                      )}
                      {row.ugeStatus === "godkendt" && (
                        <span title="Alle stemplinger i ugen er godkendt" className="text-[12px] font-semibold rounded-full px-2 py-0.5 bg-green-500/10 text-green-600 dark:text-green-400">Godkendt</span>
                      )}
                      {row.harNoteKraevet && (
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" title="Auto-udstemplet stempling uden note — kræver opmærksomhed" />
                      )}
                      {row.harAdhoc && !row.harNoteKraevet && (
                        <span title="Indeholder ad-hoc stemplinger (uden planlagt vagt)" className="text-[12px] rounded-full px-2 py-0.5 bg-secondary text-muted-foreground">ad-hoc</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-[13px]">
                      <span className="text-muted-foreground">Planlagt: <span className="font-semibold text-foreground tabular-nums">{fmtTimer(row.planlagt)}</span></span>
                      <span className="text-muted-foreground">Stemplet: <span className="font-semibold text-foreground tabular-nums">{fmtTimer(row.stemplet)}</span></span>
                      <span className="text-muted-foreground">Saldo: <span className={`font-semibold tabular-nums ${saldoFarve}`}>{row.saldo > 0 ? "+" : ""}{fmtTimer(row.saldo)}</span>
                        {row.planlagt === 0 && row.stemplet > 0 ? <span className="text-muted-foreground"> (ad-hoc)</span> : null}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {kanVælges && (
                      <button onClick={() => handleGodkendUge(row)} disabled={busy}
                        className="px-2.5 py-1 rounded-lg text-[13px] font-semibold bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50">Godkend uge</button>
                    )}
                    {kanVælges && (
                      <button onClick={() => handleAfvisUge(row)} disabled={busy}
                        className="px-2.5 py-1 rounded-lg text-[13px] font-semibold bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50">Afvis</button>
                    )}
                  </div>
                </div>

                {/* Foldet dag-for-dag breakdown */}
                {er && (
                  <div className="px-4 pb-3 pl-14 space-y-1">
                    {row.ugeDage.map(iso => {
                      const dv = row.perDag[iso].vagter;
                      const dr = row.perDag[iso].records;
                      if (dv.length === 0 && dr.length === 0) return null;
                      const planlagtTekst = dv.length ? dv.map(v => (v.fra && v.til) ? `${v.fra}–${v.til}` : (v.vagttype || "vagt")).join(", ") : null;
                      return (
                        <div key={iso} className="flex items-start gap-2 text-[13px]">
                          <span className="w-14 shrink-0 text-muted-foreground">{dagLabel(iso)}:</span>
                          <div className="flex-1">
                            {dr.length > 0 ? dr.map(r => {
                              const okGodkendt = APPROVED.has(r.status);
                              const noteKraevet = r.auto_udstemplet && !((r.note || "").trim());
                              return (
                                <div key={r.clock_id} className="flex items-center gap-1.5 flex-wrap">
                                  <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                                  <span className="tabular-nums">{fmtTid(r.clock_in)}–{fmtTid(r.clock_out)}</span>
                                  {r.er_adhoc || !r.vagt_id ? <span className="text-muted-foreground">(ad-hoc)</span> : planlagtTekst ? <span className="text-muted-foreground">(planlagt {planlagtTekst})</span> : null}
                                  {r.auto_udstemplet && <span title="Auto-udstemplet ved dagens afslutning" className="text-amber-600 dark:text-amber-400">· auto-udstemplet</span>}
                                  {okGodkendt && <Check className="w-3 h-3 text-green-500" title="Godkendt" />}
                                  {noteKraevet && <span className="text-amber-600 dark:text-amber-400 inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> note krævet</span>}
                                  {r.note && <span className="text-muted-foreground italic" title={r.note}>· note</span>}
                                  <button onClick={() => setEditRecord(r)} className="text-muted-foreground hover:text-[#FF2A6D] underline-offset-2 hover:underline">rediger</button>
                                </div>
                              );
                            }) : (
                              <span className="text-muted-foreground">planlagt {planlagtTekst} <span className="text-amber-600 dark:text-amber-400">· ingen stempling</span></span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk-handlinger */}
      {synligeRækker.some(r => r.afventendeIds.length > 0) && (
        <div className="flex items-center gap-2 mt-4">
          <button onClick={valgAlle} className="h-8 px-3 rounded-lg text-[13px] font-semibold border border-border hover:bg-secondary transition-colors">
            {valgte.size > 0 ? "Fravælg alle" : "Vælg alle"}
          </button>
          <button onClick={handleBulkGodkend} disabled={busy || valgte.size === 0}
            className="h-8 px-3 rounded-lg text-[13px] font-semibold bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-40 inline-flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Godkend valgte
          </button>
          <button onClick={handleBulkAfvis} disabled={busy || valgte.size === 0}
            className="h-8 px-3 rounded-lg text-[13px] font-semibold bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-40 inline-flex items-center gap-1.5">
            <XCircle className="w-3.5 h-3.5" /> Afvis valgte
          </button>
          {valgte.size > 0 && <span className="text-[13px] text-muted-foreground">{valgte.size} valgt</span>}
        </div>
      )}
    </>
  );
}

export default function TimegodkendelsePage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subTab, setSubTab] = useState(() => {
    const t = searchParams.get("tab");
    return t === "stempling" ? "stempling" : "timesedler";
  });

  const handleLogout = () => { logout(); navigate("/login"); };
  const isAdmin = user && ["admin", "superadmin", "owner", "api_admin"].includes(user.role);

  useEffect(() => {
    const kanGodkende = user?.rettigheder?.kan_godkende_stempling === true;
    if (!user || (!isAdmin && !kanGodkende)) { navigate("/"); return; }
  }, [user, navigate, isAdmin]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-5">
          <h1 className="text-2xl font-bold tracking-tight mb-1">Timegodkendelse</h1>
          <p className="text-sm text-muted-foreground">Godkend timer og stemplinger — ugevis (Timesedler) eller pr. stempling.</p>
        </div>

        {/* Underfaner */}
        <div className="flex items-center gap-2 mb-6 border-b border-border">
          {[
            { key: "timesedler", label: "Timesedler" },
            { key: "stempling", label: "Stempling" },
          ].map(t => (
            <button key={t.key} onClick={() => { setSubTab(t.key); setSearchParams(p => { const n = new URLSearchParams(p); n.set("tab", t.key); return n; }, { replace: true }); }}
              className={`px-1 pb-3 -mb-px text-sm font-semibold border-b-2 transition-colors ${subTab === t.key ? "border-[#FF2A6D] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {subTab === "timesedler"
          ? <TimesedlerTab token={token} isAdmin={isAdmin} />
          : <StemplingTab token={token} />}
      </div>
    </div>
  );
}
